import { useRef, useEffect, useState } from "react";
import {
  GlobalConfiguration,
  IntervalConfiguration,
} from "../../../../utils/configurationProvider";
import SvgComponent from "../../SVGComponent";
import SVGComponent from "../../SVGComponent";
import TickerAnimation from "../../TickerAnimation";

// Suggestions of props for component while using the components -------------------------------------
/**
 * @typedef {((e: React.MouseEvent<HTMLButtonElement, MouseEvent>?) => void)} OnClickEventHandler
 */
/**
 * @typedef {("big" | "small")} DropdownType
 * @param {Object} props
 * @param {Array} props.options - Default options to show
 * @param {String} props.selected - Selected option value state
 * @param {Function} props.setSelected - State setter function for option value
 * @param {DropdownType} props.type - Dropdown type
 * @param {boolean} props.isOpen - State to open or close dropdown
 * @param {boolean} props.showSearch - State to display or hide search field
 * @param {String} props.placeholder - Default placeholder for dropdown
 * @param {String} props.searchPlaceholder - Default search placeholder
 * @param {boolean} props.disabled - Checks wheather dropdown is disabled or not
 * @param {Function} props.onOpen - Callback for when this dropdown is opened
 * @param {Function} props.onClose - Callback for when this dropdown is closed
 * @returns {JSX.Element}
 */

const SingleSelectDropDownComponent = ({
  options,
  selected,
  setSelected,
  isOpen,
  showSearch,
  placeholder,
  searchPlaceholder,
  disabled,
  type,
  onOpen,
  onClose,
  statusPop = false,
  dropdownType,
  isStatistic = false,
  statisticDisplayName = false,
  showCloseButton = true,
  setCloseValue = () => {},
  enableCloseClick = false,
}) => {
  const singleSelectDropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const optionRefs = useRef([]);
  const dropdownOptionRef = useRef(null);
  const [overflowFlags, setOverflowFlags] = useState([]);
  const [dropDownOptions, setDropDownOptions] = useState([]);
  const [dropdown, setDropdown] = useState(isOpen);
  const [hasSearchValue, setHasSearchValue] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);

  useEffect(() => {
    setDropDownOptions(options);
  }, [options, dropdown]);

  // Sync internal dropdown state with external isOpen prop
  useEffect(() => {
    setDropdown(isOpen);
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        singleSelectDropdownRef.current &&
        !singleSelectDropdownRef.current?.contains(event.target)
      ) {
        setDropdown(false);
        onClose?.call(); // Call onClose when dropdown is closed by clicking outside
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [onClose]);

  const handleChange = (e) => {
    e.stopPropagation();
    const value = e.target.value;
    setHasSearchValue(value.trim() !== "");
    const filtered = options.filter((val) =>
      val.toLowerCase().includes(value.toLowerCase()),
    );
    setDropDownOptions(filtered);
  };

  const handleClearSearch = (e) => {
    e.stopPropagation();
    if (searchInputRef.current) {
      searchInputRef.current.value = "";
    }
    setHasSearchValue(false);
    setDropDownOptions(options);
  };

  const getFontSize = (type) => {
    switch (type) {
      case "small":
        return "text-font-xs";
      case "big":
        return "text-font-md";
      default:
        return "";
    }
  };

  const getMaxHeight = (type) => {
    switch (type) {
      case "small":
        return "max-h-dropdown-option-height-sm";
      case "big":
        return "max-h-dropdown-option-height-md";
      default:
        return "";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
      case "require approval":
        return "var(--pending)";
      case "approved":
        return "var(--awaiting)";
      case "rejected":
      case "blocked":
      case "Blocked":
        return "var(--failed)";
      case "archived":
      case "Discarded":
        return "var(--abandoned)";
      case "hold":
      case "queued":
        return "var(--queued)";
      case "settled":
        return "var(--settled)";
      case "all":
        return "var(--text-primary)";
      case "processed":
        return "var(--settled)";
      case "processing":
        return "var(--queued)";
      case "failed":
        return "var(--failed)";
      case "Validation pending":
      case "Pending":
      case "Require validation":
      case "Approval pending":
      case "Require approval":
      case "Authentication pending":
      case "Require authentication":
        return "var(--pending)";
      case "Process failed":
      case "Approval rejected":
        return "var(--failed)";
      case "Validation rejected":
      case "Distribution rejected":
      case "distribution rejected":
      case "distribution failed":
      case "distribution reversal":
      case "distribution denied":
        return "var(--failed)";
      case "Authenticated":
        return "var(--awaiting)";
      case "Processing":
      case "created":
      case "Distribution created":
        return "var(--queued)";
      case "Awaiting funds":
      case "distribution created":
        return "var(--queued)";
      case "distribution approved":
        return "var(--awaiting)";
      case "distribution pending":
      case "awaiting funds":
      case "Distribution initiated":
      case "distribution initiated":
        return "var(--queued)";
      case "Distribution complete":
      case "distribution complete":
        return "var(--settled)";
      case "abandoned":
      case "Abandoned":
      case "distribution abandoned":
        return "var(--abandoned)";
      default:
        return "var(--text-primary)";
    }
  };

  useEffect(() => {
    if (dropdown) {
      const flags = dropDownOptions?.map((_, i) => {
        const el = optionRefs.current[i];
        return el?.scrollWidth > el?.offsetWidth;
      });
      setOverflowFlags(flags);
    }
  }, [dropdown, dropDownOptions]);

  const upArrowIcon =
    "https://q2pay.in/new_assets/common/arrows/arrow_up_white.svg";

  const downArrowIcon =
    "https://q2pay.in/new_assets/common/arrows/arrow_down_white.svg";

  const allChecker =
    "https://q2pay.in/new_assets/common/view_all/check_mark_white.svg";

  // const getClearIconDisplay = (statusPop, selected) => {
  //   switch (true) {
  //     case !statusPop &&
  //       selected?.length > GlobalConfiguration?.lengthCheckZero:
  //       return "flex";

  //     default:
  //       return "none";
  //   }
  // };

  const hasValue = (val) => {
    if (val === null || val === undefined) return false;

    if (Array.isArray(val))
      return val.length > GlobalConfiguration?.lengthCheckZero;

    if (typeof val === "number") return true;

    if (typeof val === "string")
      return val.trim().length > GlobalConfiguration?.lengthCheckZero;

    return false;
  };

  const getClearIconDisplay = (statusPop, selected) => {
    if (isStatistic) return "none";
    if (!statusPop && hasValue(selected)) return "flex";

    if (statusPop && hasValue(selected) && selected !== "all") return "flex";

    return "none";
  };

  return (
    <div className="w-full">
      <div
        ref={singleSelectDropdownRef}
        className={`flex flex-col relative w-full open ${
          disabled
            ? "cursor-not-allowed opacity-light-opacity"
            : "cursor-pointer opacity-base-opacity"
        } ${getMaxHeight(type)}`}
        onClick={() => {
          if (!disabled) {
            if (dropdown) {
              setDropdown(false);
              setTimeout(() => {
                setOpenUpward(false);
              }, IntervalConfiguration.intervalMid);
              onClose?.call();
            } else {
              const containerRect = document
                .getElementById("root")
                ?.getBoundingClientRect();
              const cardRect =
                dropdownOptionRef?.current?.getBoundingClientRect();
              const willOverflowBottom =
                cardRect?.bottom > containerRect?.bottom &&
                cardRect?.top >= cardRect?.height;

              setOpenUpward(willOverflowBottom);
              setDropdown(true);
              onOpen?.call();
            }
          }
        }}
      >
        <div
          className={`bg-primary-nav-act ${getMaxHeight(type)} ${
            !disabled && !dropdown && "global-hover-white"
          } z-z-index-base relative flex items-center justify-between w-full px-padding-md py-padding-sm gap-gap-sm dropdown_header
    ${dropdown ? `${openUpward ? (isStatistic ? "" : "rounded-b-border-radius-md shadow-box-shadow-right") : isStatistic ? "" : "dropdown_border_bottom_radius_none shadow-box-shadow-right"}` : !isStatistic ? `${openUpward ? "rounded-border-radius-md delay-[var(--animation-fast)]" : "dropdown_border-radius-md"}` : ""}`}
        >
          <span
            className={`${getFontSize(type)} truncate select-none ${dropdownType ? dropdownType : "sentence-case"}`}
            style={{
              color:
                selected === "" && !isStatistic
                  ? "var(--text-secondary)"
                  : statusPop
                    ? getStatusColor(selected)
                    : "var(--text-primary)",
            }}
          >
            {selected === ""
              ? placeholder
              : isStatistic && statisticDisplayName
                ? statisticDisplayName
                : selected}
          </span>
          <div className="flex flex-row gap-gap-sm items-center flex-shrink-0">
            <img
              onClick={(e) => {
                e.stopPropagation();
                if (enableCloseClick) {
                  setCloseValue();
                  return;
                }
                if (!disabled) {
                  setSelected("");
                }
              }}
              src="https://q2pay.in/new_assets/common/create_flow/close.svg"
              alt="down"
              className="h-icon-xs w-icon-xs"
              style={{
                display: !showCloseButton
                  ? "none"
                  : getClearIconDisplay(statusPop, selected),
              }}
            />
            <div
              style={{
                display: !showCloseButton
                  ? "none"
                  : getClearIconDisplay(statusPop, selected),
              }}
              className="border-l max-h-dropdown-divider-height h-screen items-center border-primary-bg"
            ></div>
            <div
              className={`transition-transform duration-animation-fast ease-in-out ${
                dropdown ? "rotate-half" : "rotate-base"
              }`}
            >
              <SvgComponent
                url={downArrowIcon}
                color="var(--text-primary)"
                width="var(--icon-xs)"
                height="var(--icon-xs)"
              />
            </div>
          </div>
        </div>

        <div className="relative">
          <div
            className={`bg-primary-nav-act ${dropdown ? `${openUpward ? "shadow-box-shadow-right-top" : "shadow-box-shadow-right-bottom"}` : ""} ${openUpward ? `${type === "small" ? "bottom-[var(--dropdown-option-height-sm)]" : "bottom-[var(--dropdown-option-height-md)]"} flex flex-col-reverse rounded-t-border-radius-md` : "rounded-b-border-radius-md"}  absolute z-z-index-dropdown overflow-hidden w-width-full `}
            style={{
              maxHeight: dropdown
                ? type == "big"
                  ? "var(--max-animation-height-big-ddl)"
                  : "var(--max-animation-height-small-ddl)"
                : "0px",
              transition: "max-height var(--animation-fast) ease-in-out",
            }}
          >
            <div
              className="border-primary-bg border-t mx-margin-sm transition-all duration-[var(--animation-fast)]"
              style={{
                opacity: !dropdown
                  ? "var(--opacity-none)"
                  : "var(--base-opacity)",
              }}
            ></div>
            {showSearch && (
              <div className="bg-primary-nav-act px-padding-sm pt-padding-sm w-full">
                <div
                  className={`rounded-border-radius-md flex items-center px-padding-sm py-padding-sm opacity-base-opacity bg-primary-bg ${getMaxHeight(
                    type,
                  )}`}
                >
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      searchInputRef.current?.focus();
                    }}
                  >
                    <SVGComponent
                      url={
                        "https://q2pay.in/new_assets/common/view_all/search_white.svg"
                      }
                      color="var(--text-primary)"
                      height={"var(--icon-sm)"}
                      width={"var(--icon-sm)"}
                    />
                  </div>
                  <input
                    className={`w-full outline-none bg-transparent text-left ${getFontSize(
                      type,
                    )} font-normal ml-margin-sm text-text-secondary border-text-primary placeholder-text-secondary`}
                    ref={searchInputRef}
                    type="text"
                    onChange={handleChange}
                    onClick={(e) => e.stopPropagation()}
                    placeholder={searchPlaceholder}
                  />
                  {hasSearchValue && (
                    <img
                      src="https://q2pay.in/new_assets/common/dashboard/close_with_white_circle.svg"
                      alt="clear"
                      className="h-icon-sm w-icon-sm cursor-pointer"
                      onClick={handleClearSearch}
                    />
                  )}
                </div>
              </div>
            )}

            <div
              ref={dropdownOptionRef}
              className={`flex flex-col pt-padding-sm pl-padding-sm pb-padding-base ${
                type == "big"
                  ? "max-h-max-overflow-height-big-ddl-options"
                  : "max-h-max-overflow-height-small-ddl-options"
              } bg-primary-nav-act w-full rounded-b-border-radius-md overflow-auto border-t-border-base cursor-pointer gap-gap-sm`}
            >
              <div className="flex flex-col pr-padding-sm bg-primary-nav-act w-full overflow-auto gap-gap-sm mb-margin-sm">
                {dropDownOptions?.map((val, index) => {
                  return (
                    <div
                      key={index}
                      title={
                        overflowFlags[index]
                          ? isStatistic
                            ? val.name
                            : val
                          : ""
                      }
                      className={`relative max-w-full gap-gap-sm ${getMaxHeight(
                        type,
                      )} z-z-index-base global-hover-white py-padding-sm px-padding-sm truncate flex justify-between items-center rounded-border-radius-md flex-shrink-0 ${getFontSize(
                        type,
                      )} ${isStatistic ? (selected === val.value ? "bg-primary-nav" : "") : selected === val ? "bg-primary-nav" : ""}`}
                      onClick={() => {
                        if (isStatistic) {
                          setSelected(val);
                        } else {
                          setSelected(val);
                        }
                      }}
                    >
                      <p
                        ref={(el) => (optionRefs.current[index] = el)}
                        className={`truncate ${dropdownType ? dropdownType : "sentence-case"}`}
                        style={{
                          color: statusPop
                            ? getStatusColor(val)
                            : "var(--text-primary)",
                        }}
                      >
                        <TickerAnimation text={isStatistic ? val.name : val} />
                      </p>
                      {(selected === val ||
                        (isStatistic && selected === val.value)) && (
                        <SvgComponent
                          url={allChecker}
                          height="var(--icon-sm)"
                          width="var(--icon-sm)"
                          color="var(--text-primary)"
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SingleSelectDropDownComponent;
